import { useState, useCallback } from "react";
import type {
  ProviderViewModel,
  CreateProviderRequest,
  UpdateProviderRequest,
  CredentialViewModel,
  CreateCredentialRequest,
  UpdateCredentialRequest,
  PolicyViewModel,
  CreatePolicyRequest,
  UpdatePolicyRequest,
} from "@models/provider";
import type { ModelViewModel } from "@models/model";
import { providerApi } from "@services/providerApi";

export const useProvider = () => {
  const [providers, setProviders] = useState<ProviderViewModel[]>([]);
  const [models, setModels] = useState<Map<string, ModelViewModel[]>>(
    new Map(),
  );
  const [credentials, setCredentials] = useState<
    Map<string, CredentialViewModel[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await providerApi.getProviders({ limit: 200 });
      setProviders(response.items);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch providers",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchModels = useCallback(async (providerId: string) => {
    try {
      const response = await providerApi.getModelsByProvider(providerId, {
        limit: 200,
      });
      setModels((prev) => new Map(prev).set(providerId, response.items));
    } catch (err) {
      console.error("Failed to fetch models:", err);
    }
  }, []);

  const fetchCredentials = useCallback(async (providerId: string) => {
    try {
      const items = await providerApi.getCredentialsByProvider(providerId);
      setCredentials((prev) => new Map(prev).set(providerId, items));
    } catch (err) {
      console.error("Failed to fetch credentials:", err);
    }
  }, []);

  const createProvider = useCallback(
    async (data: CreateProviderRequest) => {
      try {
        setIsLoading(true);
        setError(null);
        await providerApi.createProvider(data);
        await fetchProviders();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to create provider";
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchProviders],
  );

  const updateProvider = useCallback(
    async (id: string, data: UpdateProviderRequest) => {
      try {
        setIsLoading(true);
        setError(null);
        await providerApi.updateProvider(id, data);
        await fetchProviders();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to update provider";
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchProviders],
  );

  const deleteProvider = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        setError(null);
        await providerApi.deleteProvider(id);
        await fetchProviders();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to delete provider";
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchProviders],
  );

  const createCredential = useCallback(
    async (providerId: string, data: CreateCredentialRequest) => {
      await providerApi.createCredential(providerId, data);
      await fetchCredentials(providerId);
    },
    [fetchCredentials],
  );

  const updateCredential = useCallback(
    async (
      providerId: string,
      credentialId: string,
      data: UpdateCredentialRequest,
    ) => {
      await providerApi.updateCredential(credentialId, data);
      await fetchCredentials(providerId);
    },
    [fetchCredentials],
  );

  const deleteCredential = useCallback(
    async (providerId: string, credentialId: string) => {
      await providerApi.deleteCredential(credentialId);
      await fetchCredentials(providerId);
    },
    [fetchCredentials],
  );

  return {
    providers,
    models,
    credentials,
    isLoading,
    error,
    fetchProviders,
    fetchModels,
    fetchCredentials,
    createProvider,
    updateProvider,
    deleteProvider,
    createCredential,
    updateCredential,
    deleteCredential,
  };
};

export const usePolicy = () => {
  const [policies, setPolicies] = useState<PolicyViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPolicies = useCallback(async (scopeKey?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const items = await providerApi.getPolicies(scopeKey);
      setPolicies(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch policies");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createPolicy = useCallback(
    async (data: CreatePolicyRequest) => {
      await providerApi.createPolicy(data);
      await fetchPolicies();
    },
    [fetchPolicies],
  );

  const updatePolicy = useCallback(
    async (id: string, data: UpdatePolicyRequest) => {
      await providerApi.updatePolicy(id, data);
      await fetchPolicies();
    },
    [fetchPolicies],
  );

  const deletePolicy = useCallback(
    async (id: string) => {
      await providerApi.deletePolicy(id);
      await fetchPolicies();
    },
    [fetchPolicies],
  );

  return {
    policies,
    isLoading,
    error,
    fetchPolicies,
    createPolicy,
    updatePolicy,
    deletePolicy,
  };
};
